import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Snackbar,
  Tabs,
  Tab,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Add, Edit, Delete, Description, Search } from '@mui/icons-material';
import { ModuleLayout } from '../../components/Layout/ModuleLayout';
import PendingApprovalsSection from '../../components/common/PendingApprovalsSection';
import { moduleService, moduleScheduleService, ModuleSchedule } from '../../services';
import { usePermission } from '../../hooks';
import { colors } from '../../theme/theme';

export const ModuleSchedulesPage = () => {
  const { id } = useParams<{ id: string }>();
  const { can } = usePermission();
  const [module, setModule] = useState<any>(null);
  const [schedules, setSchedules] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  // Filter schedules based on search query
  const filteredSchedules = schedules.filter((schedule) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      schedule.name?.toLowerCase().includes(query) ||
      schedule.description?.toLowerCase().includes(query)
    );
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [moduleRes, schedulesRes] = await Promise.all([
        moduleService.getModule(Number(id)),
        moduleScheduleService.getAllForCounty(), // Fetch all county schedules
      ]);

      if (moduleRes.status === 'success') {
        setModule(moduleRes.data);
      }
      if (schedulesRes.status === 'success' && schedulesRes.data) {
        const schedulesData = schedulesRes.data || [];
        setSchedules(Array.isArray(schedulesData) ? schedulesData : []);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (schedule?: any) => {
    if (schedule) {
      setSelectedSchedule(schedule);
      setFormData({
        name: schedule.name || '',
        description: schedule.description || '',
      });
    } else {
      setSelectedSchedule(null);
      setFormData({ name: '', description: '' });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedSchedule(null);
    setFormData({ name: '', description: '' });
  };

  const handleSubmit = async () => {
    try {
      let response;
      if (selectedSchedule) {
        response = await moduleScheduleService.update(selectedSchedule.id, formData);
        if (response.status === 'success') {
          setSnackbar({
            open: true,
            message: response.message || 'Update request submitted for approval',
            severity: 'success',
          });
          setTabValue(1);
        }
      } else {
        response = await moduleScheduleService.create(formData);
        if (response.status === 'success') {
          setSnackbar({
            open: true,
            message: 'Schedule created successfully',
            severity: 'success',
          });
        }
      }
      fetchData();
      handleCloseDialog();
    } catch (err: any) {
      console.error('Failed to save schedule:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to save schedule',
        severity: 'error',
      });
    }
  };

  const handleDelete = async (scheduleId: number) => {
    if (!window.confirm('Are you sure you want to submit a delete request for this schedule?')) return;
    try {
      const response = await moduleScheduleService.delete(scheduleId);
      if (response.status === 'success') {
        setSnackbar({
          open: true,
          message: response.message || 'Delete request submitted for approval',
          severity: 'success',
        });
        setTabValue(1);
        fetchData();
      }
    } catch (err: any) {
      console.error('Failed to delete schedule:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to delete schedule',
        severity: 'error',
      });
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      width: 300,
      display: 'flex',
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 400,
      display: 'flex',
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" color="text.secondary">
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      display: 'flex',
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value || 'Active'}
          color="success"
          size="small"
          sx={{ fontWeight: 600 }}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {can('schedules.manage') && (
            <IconButton
              size="small"
              onClick={() => handleOpenDialog(params.row)}
              sx={{ color: colors.primary.main }}
            >
              <Edit fontSize="small" />
            </IconButton>
          )}
          {can('schedules.manage') && (
            <IconButton
              size="small"
              onClick={() => handleDelete(params.row.id)}
              sx={{ color: '#dc2626' }}
            >
              <Delete fontSize="small" />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <ModuleLayout module={module} moduleId={id!}>
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      </ModuleLayout>
    );
  }

  return (
    <ModuleLayout module={module} moduleId={id!}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1f2937', mb: 1 }}>
              Schedules
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Manage payment schedules (available across all modules)
            </Typography>
          </Box>
          {can('schedules.manage') && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{
                background: colors.gradients.primary,
                boxShadow: colors.shadows.button,
                '&:hover': {
                  background: colors.gradients.primaryHover,
                  boxShadow: colors.shadows.buttonHover,
                  transform: 'translateY(-1px)',
                },
              }}
            >
              Add Schedule
            </Button>
          )}
        </Box>

        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Schedules" />
          <Tab label="Approvals" />
        </Tabs>

        {tabValue === 0 && (
          <>
            <TextField
              fullWidth
              placeholder="Search schedules by name or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
              }}
            />

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ height: 600, width: '100%' }}>
              <DataGrid
                rows={filteredSchedules}
                columns={columns}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[5, 10, 25, 50, 100]}
                disableRowSelectionOnClick
                sx={{
                  '& .MuiDataGrid-cell:focus': {
                    outline: 'none',
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
                slots={{
                  noRowsOverlay: () => (
                    <Box sx={{ textAlign: 'center', py: 8, mt: 4 }}>
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          bgcolor: colors.primary[50],
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 'auto',
                          mb: 3,
                        }}
                      >
                        <Description sx={{ fontSize: 40, color: colors.primary.main }} />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        No Schedules Found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Create your first payment schedule (will be available across all modules)
                      </Typography>
                      {can('schedules.manage') && (
                        <Button
                          variant="contained"
                          startIcon={<Add />}
                          onClick={() => handleOpenDialog()}
                          sx={{
                            background: colors.gradients.primary,
                          }}
                        >
                          Add Schedule
                        </Button>
                      )}
                    </Box>
                  ),
                }}
              />
            </Box>
          </>
        )}

        {tabValue === 1 && (
          <PendingApprovalsSection
            entityType="schedule"
            title="Pending Schedule Approvals"
            onApprovalComplete={fetchData}
          />
        )}

        {/* Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {selectedSchedule ? 'Edit Schedule' : 'Add Schedule'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                fullWidth
                required
                label="Schedule Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{
                background: colors.gradients.primary,
              }}
            >
              {selectedSchedule ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </ModuleLayout>
  );
};
